import { ICatalog, IDeal, INutrition, IOptionList, IPriceOverride, IProduct, IRestrictions } from "./models"
import { Category, Sku, OptionList } from "./utils"

export interface ExportedCatalog {
  data: {
    variants: Array<ExportedVariant>
    categories: Array<ExportedCategory>
    products: Array<ExportedProduct>
    option_lists: Array<ExportedOptionList>
    deals: Array<ExportedDeal>
    discounts: Array<ExportedDiscount>
    charges: Array<ExportedCharge>
  }
}

export interface ExportedVariant {
  ref: string
  name: string
}

export interface ExportedRestrictions {
  enabled?: boolean
  variant_refs?: Array<string>
  dow?: string
  start_time?: string
  end_time?: string
  start_date?: string
  end_date?: string
  service_types?: Array<string>
  service_type_refs?: Array<string>
  min_order_amount?: string
  max_per_order?: number
  max_per_customer?: number
}

export interface ExportedPriceOverride {
  variant_refs?: Array<string>
  dow?: string
  start_time?: string
  end_time?: string
  start_date?: string
  end_date?: string
  service_types?: Array<string>
  service_type_refs?: Array<string>
  price: string
}

export interface ExportedCategory {
  ref: string
  parent_ref: string | null
  name: string
  description: string | null
  tags: Array<string>
  image_ids: Array<string>
}

export interface ExportedProduct {
  name: string
  description: string | null
  category_ref: string
  tags: Array<string>
  nutrition: ExportedNutrition | null
  tax_rate: {
    delivery: string
    collection: string
    eat_in: string
  } | null
  image_ids: Array<string>
  skus: Array<ExportedSku>
}

interface ExportedPer100Energy {
  unit: string
  lower: string
  upper: string | null
}

interface ExportedPer100Values {
  per: string
  energy: ExportedPer100Energy | null
  fat: string | null
  saturated_fat: string | null
  mono_unsaturated_fat: string | null
  poly_unsaturated_fat: string | null
  carbohydrate: string | null
  sugar: string | null
  polyols: string | null
  starch: string | null
  fibre: string | null
  protein: string | null
  salt: string | null
}

interface ExportedNutrition {
  legal_name: string | null
  ingredients: string[] | null
  additives: string[] | null
  allergens: string[] | null
  per_100_values: ExportedPer100Values | null
  alcohol_percentage: string | null
  caffeine_mg_per_100ml: string | null
  producer_name: string | null
  producer_brand: string | null
  producer_address: string | null
  country_of_origin: string | null
}

export interface ExportedSku {
  ref: string | null
  name: string | null
  restrictions?: ExportedRestrictions
  price: string
  price_overrides?: Array<ExportedPriceOverride>
  option_list_refs: Array<string>
  tags: Array<string>
  barcodes: Array<string>
  weight: string | null
  volume: string | null
  nb_servings: number | null
}

export interface ExportedOptionList {
  ref: string
  name: string
  min_selections: number
  max_selections: number | null
  multiple_selection: boolean
  tags: Array<string>
  options: Array<ExportedOption>
}

export interface ExportedOption {
  ref: string | null
  name: string
  restrictions?: ExportedRestrictions
  price: string
  price_overrides?: Array<ExportedPriceOverride>
  default: boolean
  tags: Array<string>
  nutrition: ExportedNutrition | null
  weight: string | null
  volume: string | null
}

export interface ExportedDeal {
  ref: string | null
  category_ref: string | null
  name: string
  description: string | null
  restrictions?: ExportedRestrictions
  coupon_codes: Array<string>
  tags: Array<string>
  image_ids: Array<string>
  lines: Array<ExportedDealLine>
}

export interface ExportedDealLine {
  label: string | null
  pricing_effect: string
  pricing_value: string | null
  skus: Array<{
    ref: string
    extra_charge: string | null
  }>
}

export interface ExportedDiscount {
  ref: string | null
  name: string
  description: string | null
  restrictions?: ExportedRestrictions
  coupon_codes: Array<string>
  pricing_effect: string
  pricing_value: string
  image_ids: Array<string>
}

export interface ExportedCharge {
  ref: string | null
  name: string
  type: string
  restrictions?: ExportedRestrictions
  price: string | null
}

const exportVariants = (catalog: ICatalog): Array<ExportedVariant> => {
  return catalog.data.variants.map((variant) => ({
    ref: variant.ref,
    name: variant.name,
  }))
}

const exportCategories = (catalog: ICatalog): Array<ExportedCategory> => {
  return catalog.data.categories.map((category) => {
    const { name, description, tags, imageHrIds } = category
    const ref = Category.ref(category)

    const parent_ref: string | null =
      category.parentId !== null ? Category.ref(Category.byId(catalog, category.parentId)) : null

    const exportedCategory: ExportedCategory = {
      ref,
      parent_ref,
      name,
      description: description || null,
      tags,
      image_ids: imageHrIds,
    }
    return exportedCategory
  })
}

const exportProducts = (catalog: ICatalog): Array<ExportedProduct> => {
  return catalog.data.products.map((product) => {
    const { name, description, tags, imageHrIds } = product

    const skus = exportSkus(catalog, product)
    const category_ref: string = Category.ref(Category.byId(catalog, product.categoryId))

    const exportedProduct: ExportedProduct = {
      name,
      description: description || null,
      category_ref,
      tags: [...tags, ...product.legacyAllergenTags],
      nutrition: exportNutrition(product.nutrition),
      tax_rate: product.taxRate
        ? {
            delivery: product.taxRate.delivery.toString(),
            collection: product.taxRate.collection.toString(),
            eat_in: product.taxRate.eatIn.toString(),
          }
        : null,
      image_ids: imageHrIds,
      skus,
    }
    return exportedProduct
  })
}

const exportRestrictions = (restrictions: IRestrictions): ExportedRestrictions | undefined => {
  const {
    enabled,
    variantRefs,
    dow,
    startTime,
    endTime,
    startDate,
    endDate,
    serviceTypes,
    serviceTypeRefs,
    minOrderAmount,
    maxPerOrder,
    maxPerCustomer,
  } = restrictions

  // Simplifying this code without introducing TS errors is not trivial...
  const exportedRestrictions: ExportedRestrictions = {}
  if (!enabled) exportedRestrictions.enabled = false
  if (variantRefs) exportedRestrictions.variant_refs = variantRefs
  if (dow !== undefined) exportedRestrictions.dow = dow
  if (startTime !== undefined) exportedRestrictions.start_time = startTime
  if (endTime !== undefined) exportedRestrictions.end_time = endTime
  if (startDate !== undefined) exportedRestrictions.start_date = startDate
  if (endDate !== undefined) exportedRestrictions.end_date = endDate
  if (serviceTypes !== undefined) exportedRestrictions.service_types = serviceTypes
  if (serviceTypeRefs !== undefined) exportedRestrictions.service_type_refs = serviceTypeRefs
  if (minOrderAmount !== undefined) exportedRestrictions.min_order_amount = minOrderAmount
  if (maxPerOrder !== undefined) exportedRestrictions.max_per_order = maxPerOrder
  if (maxPerCustomer !== undefined) exportedRestrictions.max_per_customer = maxPerCustomer
  return Object.keys(exportedRestrictions).length > 0 ? exportedRestrictions : undefined
}

const exportPriceOverrides = (priceOverrides: Array<IPriceOverride>): Array<ExportedPriceOverride> | undefined => {
  if (priceOverrides.length === 0) return undefined

  return priceOverrides.map((priceOverride) => {
    const { variantRefs, dow, startTime, endTime, startDate, endDate, serviceTypes, serviceTypeRefs, price } =
      priceOverride

    const exportedPriceOverride: ExportedPriceOverride = {
      variant_refs: variantRefs,
      dow,
      start_time: startTime,
      end_time: endTime,
      start_date: startDate,
      end_date: endDate,
      service_types: serviceTypes,
      service_type_refs: serviceTypeRefs,
      price: price.toString(),
    }
    return exportedPriceOverride
  })
}

const exportNutrition = (nutrition: INutrition | null): ExportedNutrition | null => {
  if (!nutrition) return null
  return {
    ...nutrition,
    per_100_values: nutrition.per_100_values
      ? {
          ...nutrition.per_100_values,
          energy: nutrition.per_100_values.energy
            ? {
                unit: nutrition.per_100_values.energy.unit,
                lower: nutrition.per_100_values.energy.lower.toString(),
                upper: nutrition.per_100_values.energy.upper?.toString() ?? null,
              }
            : null,
        }
      : null,
    alcohol_percentage: nutrition.alcohol_percentage?.toString() ?? null,
    caffeine_mg_per_100ml: nutrition.caffeine_mg_per_100ml?.toString() ?? null,
  }
}

const exportSkus = (catalog: ICatalog, product: IProduct): Array<ExportedSku> => {
  return product.skus.map((sku) => {
    const { ref, name, restrictions, price, priceOverrides, optionListIds, tags, barcodes, weight, volume } = sku

    const option_list_refs: Array<string> = optionListIds.map((id) => OptionList.ref(OptionList.byId(catalog, id)))

    const exportedSku: ExportedSku = {
      ref: ref || null,
      name: name || null,
      restrictions: exportRestrictions(restrictions),
      price: price.toString(),
      price_overrides: exportPriceOverrides(priceOverrides),
      option_list_refs,
      tags,
      barcodes,
      weight,
      volume,
      nb_servings: sku.nbServings,
    }
    return exportedSku
  })
}

const exportOptionLists = (catalog: ICatalog): Array<ExportedOptionList> => {
  return catalog.data.optionLists.map((optionList) => {
    const { name, min_selections, max_selections, multiple_selection, tags } = optionList
    const ref = OptionList.ref(optionList)

    const options = exportOptions(catalog, optionList)

    const exportedOptionList: ExportedOptionList = {
      ref,
      name,
      min_selections,
      max_selections,
      multiple_selection,
      tags,
      options,
    }
    return exportedOptionList
  })
}

const exportOptions = (catalog: ICatalog, optionList: IOptionList): Array<ExportedOption> => {
  return optionList.options.map((option) => {
    const { ref, name, restrictions, price, priceOverrides, isDefault, tags, nutrition, weight, volume } = option

    const exportedOption: ExportedOption = {
      ref: ref || null,
      name: name,
      restrictions: exportRestrictions(restrictions),
      price: price.toString(),
      price_overrides: exportPriceOverrides(priceOverrides),
      default: isDefault,
      tags,
      nutrition: exportNutrition(nutrition),
      weight,
      volume,
    }
    return exportedOption
  })
}

const exportDeals = (catalog: ICatalog): Array<ExportedDeal> => {
  return catalog.data.deals.map((deal) => {
    const { ref, categoryId, name, description, restrictions, couponCodes, tags, imageHrIds } = deal

    const dealLines = exportDealLines(catalog, deal)
    const category_ref: string | null = categoryId !== null ? Category.ref(Category.byId(catalog, categoryId)) : null

    const exportedDeal: ExportedDeal = {
      ref,
      category_ref,
      name,
      description: description || null,
      restrictions: exportRestrictions(restrictions),
      coupon_codes: couponCodes,
      tags,
      image_ids: imageHrIds,
      lines: dealLines,
    }
    return exportedDeal
  })
}

const exportDealLines = (catalog: ICatalog, deal: IDeal): Array<ExportedDealLine> => {
  return deal.lines.map((dealLine) => {
    const { label, pricingEffect, pricingValue, skus } = dealLine

    const exportedSkus: ExportedDealLine["skus"] = skus.map(({ skuId, extraCharge }) => ({
      ref: Sku.byId(catalog, skuId).ref,
      extra_charge: extraCharge === null ? null : extraCharge.toString(),
    }))

    return {
      label: label || null,
      pricing_effect: pricingEffect,
      pricing_value: pricingValue === null ? null : pricingValue.toString(),
      skus: exportedSkus,
    }
  })
}

const exportDiscounts = (catalog: ICatalog): Array<ExportedDiscount> => {
  return catalog.data.discounts.map((discount) => {
    const { ref, name, description, restrictions, couponCodes, pricingEffect, pricingValue, imageHrIds } = discount

    const exportedDiscount: ExportedDiscount = {
      ref,
      name,
      description: description || null,
      restrictions: exportRestrictions(restrictions),
      coupon_codes: couponCodes,
      pricing_effect: pricingEffect,
      pricing_value: pricingValue.toString(),
      image_ids: imageHrIds,
    }
    return exportedDiscount
  })
}

const exportCharges = (catalog: ICatalog): Array<ExportedCharge> => {
  return catalog.data.charges.map((charge) => {
    const { ref, name, restrictions, type, price } = charge

    const exportedCharge: ExportedCharge = {
      ref,
      name,
      type,
      restrictions: exportRestrictions(restrictions),
      price: price === null ? null : price.toString(),
    }
    return exportedCharge
  })
}

const exportCatalog = (catalog: ICatalog): ExportedCatalog => {
  return {
    data: {
      variants: exportVariants(catalog),
      categories: exportCategories(catalog),
      products: exportProducts(catalog),
      option_lists: exportOptionLists(catalog),
      deals: exportDeals(catalog),
      discounts: exportDiscounts(catalog),
      charges: exportCharges(catalog),
    },
  }
}

export default (catalog: ICatalog): ExportedCatalog => exportCatalog(catalog)
